// SpendingDashboard.js
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FeedSegments } from '../feed/FeedCard';
import AwardFeed from './AwardFeed';
import AwardMoneyPanel from './AwardMoneyPanel';
import BudgetPanel from './BudgetPanel';
import CompaniesPanel from './CompaniesPanel';
import TradesPanel from './TradesPanel';

import './Spending.css';

/**
 * SpendingDashboard
 *
 * Federal spending and the companies it pays. Rendered by
 * SpendingDashboardPage at /us/spending-dashboard, the Spending tab in the
 * bottom bar. It adds no page wrapper or width of its own; the page supplies
 * the column.
 *
 *   budget      what the government is spending on (every agency, by purpose)
 *   awards      where the award money goes (the named-recipient subset)
 *   feed        the newest awards, and the largest this month -- two here,
 *               the rest on their own page (AwardsPage)
 *   companies   the companies behind the money, each opening /us/company/:id
 *   trades      the largest stock trades members of Congress reported
 *
 * Always open. It used to minimize, and remember being minimized, because it
 * sat above the Spending topic's feeds; as a page of its own there is nothing
 * to minimize it in favour of. The header is a plain title.
 */

const PANELS = ['budget', 'awards', 'feed', 'companies', 'trades'];

function SpendingDashboard() {
  const { t } = useTranslation('common');

  const jumpLabel = (id) =>
    ({
      budget: t('common:jumpBudget', 'Budget'),
      awards: t('common:jumpAwards', 'Awards'),
      feed: t('common:jumpFeed', 'New awards'),
      companies: t('common:jumpCompanies', 'Companies'),
      trades: t('common:jumpTrades', 'Stock trades'),
    })[id];

  // Scrolled by hand, with room for the fixed navbar, rather than with an
  // anchor jump that would land the heading under it.
  const jump = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <div className="spending-dashboard">
      <h2 className="spending-dashboard-title">{t('common:federalSpending', 'Federal spending')}</h2>

      <div className="spending-dashboard-body">
        <p className="spending-lead spending-dashboard-intro">
          {t(
            'common:spendingIntro',
            'Where federal money is committed, who receives it, and the companies behind it. Figures are obligations: binding promises to pay, not payments. The fiscal year runs October to September.'
          )}
        </p>

        <FeedSegments
          className="spending-jump"
          label={t('common:jumpTo', 'Jump to')}
          value={null}
          onChange={jump}
          options={PANELS.map((id) => ({ id, label: jumpLabel(id) }))}
        />

        <BudgetPanel />
        <AwardMoneyPanel />
        {/* Two awards and a See all button; the full feed has its own page
            (AwardsPage), so it no longer stands between the reader and the
            companies and trades below. */}
        <AwardFeed preview />
        <CompaniesPanel />
        <TradesPanel />

        <p className="spending-provenance">
          {t(
            'common:spendingProvenance',
            'Budget figures come from every agency\u2019s own financial reports and move a month at a time. Awards come from USAspending and update daily. Companies are rebuilt weekly from USAspending, the SEC, Senate lobbying filings and the FEC. Trades are read weekly from the House Clerk and the Senate.'
          )}
        </p>
      </div>

      <style>{`
        /* The title the collapse button used to carry, as a plain heading. */
        .spending-dashboard-title {
          margin: 0;
          padding: 8px 0;
          font-family: var(--site-font);
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary);
        }
        @media screen and (min-width: 768px) {
          .spending-dashboard-title { font-size: 17px; }
        }
      `}</style>
    </div>
  );
}

export default SpendingDashboard;